/**
 * Helper functions to create context objects for the AI Command Palette
 */

/**
 * Create a context object for a task page
 * @param {Object} task - The task object
 * @param {Array} customLoadingTexts - Optional custom loading texts
 * @returns {Object} - The context object
 */
export const createTaskContext = (task, customLoadingTexts) => {
  if (!task || !task.key) return null;

  const defaultTaskLoadingTexts = [
    {
      text: `Looking up details for ${task.key}`,
      showOnce: true,
      minTime: 2000,
      maxTime: 3000
    },
    {
      text: `Checking status of ${task.key}`,
      showOnce: true,
      minTime: 2500,
      maxTime: 3500
    },
    {
      text: "Thinking",
      showOnce: false,
      minTime: 1500,
      maxTime: 2500
    },
    {
      text: `Processing information about ${task.summary ? `"${task.summary}"` : 'this task'}`,
      showOnce: false,
      minTime: 2800,
      maxTime: 4000
    }
  ];

  return {
    type: 'task',
    label: task.key,
    data: {
      key: task.key,
      org: task.organization.slug,
    },
    loadingTexts: customLoadingTexts || defaultTaskLoadingTexts
  };
};

/**
 * Create a context object for an organization page
 * @param {Object} organization - The organization object
 * @param {Array} customLoadingTexts - Optional custom loading texts
 * @returns {Object} - The context object
 */
export const createOrganizationContext = (organization, customLoadingTexts) => {
  if (!organization || !organization.slug) return null;

  const defaultOrgLoadingTexts = [
    {
      text: `Querying data from ${organization.name}`,
      showOnce: true,
      minTime: 2000,
      maxTime: 3000
    },
    {
      text: "Thinking",
      showOnce: false,
      minTime: 1500,
      maxTime: 2500
    },
    {
      text: `Looking through ${organization.name} information`,
      showOnce: false,
      minTime: 2000,
      maxTime: 4000
    },
    {
      text: "Processing organization data",
      showOnce: false,
      minTime: 2500,
      maxTime: 3500
    }
  ];
  
  return {
    type: 'organization',
    label: organization.name,
    data: {
      org: organization.slug
    },
    loadingTexts: customLoadingTexts || defaultOrgLoadingTexts
  };
};

/**
 * Create a context object for a project page
 * @param {Object} project - The project object
 * @param {Array} customLoadingTexts - Optional custom loading texts
 * @returns {Object} - The context object
 */
export const createProjectContext = (project, customLoadingTexts) => {
  if (!project || !project.id) return null;
  
  const defaultProjectLoadingTexts = [
    {
      text: `Querying data from ${project.name}`,
      showOnce: true,
      minTime: 2000,
      maxTime: 3000
    },
    {
      text: "Thinking",
      showOnce: false,
      minTime: 1500,
      maxTime: 2500
    },
    {
      text: `Analyzing ${project.name} tasks`,
      showOnce: false,
      minTime: 2000,
      maxTime: 4000
    },
    {
      text: "Processing project information",
      showOnce: false,
      minTime: 2500,
      maxTime: 3500
    }
  ];
  
  return {
    type: 'project',
    label: project.name,
    data: {
      key: project.key,
      org: project.organizationSlug,
    },
    loadingTexts: customLoadingTexts || defaultProjectLoadingTexts
  };
};

/**
 * Create a custom context object
 * @param {string} type - The context type
 * @param {string} label - The display label for the context
 * @param {Object} data - Additional data to include
 * @param {Array} customLoadingTexts - Optional custom loading texts
 * @returns {Object} - The context object
 */
export const createCustomContext = (type, label, data = {}, customLoadingTexts) => {
  if (!type || !label) return null;
  
  return {
    type,
    label,
    data,
    loadingTexts: customLoadingTexts
  };
}; 